// Intro state machine.
// Guarantees: cannot re-enter, phases advance in order, external timers
// cannot skip past the current phase without going through the store.
import { create } from "zustand";
import { INTRO_TIMING, STORAGE } from "@/config/game";

export const PHASE = {
    BLACK_SCREEN: "BLACK_SCREEN",
    AXIS_INTRO: "AXIS_INTRO",
    BLACK_TRANSITION: "BLACK_TRANSITION",
    GAME_TITLE_INTRO: "GAME_TITLE_INTRO",
    BLACK_TRANSITION_2: "BLACK_TRANSITION_2",
    MENU_LOADING: "MENU_LOADING",
    DONE: "DONE",
};

// Ordered sequence with total duration per phase (ms)
const SEQUENCE = [
    { phase: PHASE.BLACK_SCREEN, duration: INTRO_TIMING.BLACK_SCREEN },
    {
        phase: PHASE.AXIS_INTRO,
        duration:
            INTRO_TIMING.AXIS_INTRO_IN +
            INTRO_TIMING.AXIS_INTRO_HOLD +
            INTRO_TIMING.AXIS_INTRO_OUT,
    },
    { phase: PHASE.BLACK_TRANSITION, duration: INTRO_TIMING.BLACK_TRANSITION },
    {
        phase: PHASE.GAME_TITLE_INTRO,
        duration:
            INTRO_TIMING.GAME_TITLE_IN +
            INTRO_TIMING.GAME_TITLE_HOLD +
            INTRO_TIMING.GAME_TITLE_OUT,
    },
    { phase: PHASE.BLACK_TRANSITION_2, duration: INTRO_TIMING.BLACK_TRANSITION_2 },
    { phase: PHASE.MENU_LOADING, duration: INTRO_TIMING.MENU_LOADING },
    { phase: PHASE.DONE, duration: 0 },
];

export function hasSeenIntro() {
    try {
        return localStorage.getItem(STORAGE.seenIntro) === "1";
    } catch (e) {
        return false;
    }
}

export function markIntroSeen() {
    try {
        localStorage.setItem(STORAGE.seenIntro, "1");
    } catch (e) {}
}

export const useIntroStore = create((set, get) => ({
    phase: PHASE.BLACK_SCREEN,
    startedAt: 0,
    phaseStartedAt: 0,
    phaseIndex: 0,
    finished: false,
    skipRequested: false,
    canSkip: hasSeenIntro(), // subsequent launches can skip immediately

    start() {
        const now = performance.now();
        set({
            phase: PHASE.BLACK_SCREEN,
            startedAt: now,
            phaseStartedAt: now,
            phaseIndex: 0,
            finished: false,
            skipRequested: false,
            canSkip: hasSeenIntro(),
        });
    },

    advance() {
        const s = get();
        if (s.finished) return;
        const nextIndex = s.phaseIndex + 1;
        if (nextIndex >= SEQUENCE.length - 1) {
            // Move into DONE
            set({
                phase: PHASE.DONE,
                phaseIndex: SEQUENCE.length - 1,
                phaseStartedAt: performance.now(),
                finished: true,
            });
            markIntroSeen();
            return;
        }
        set({
            phase: SEQUENCE[nextIndex].phase,
            phaseIndex: nextIndex,
            phaseStartedAt: performance.now(),
        });
    },

    // Skip: jump straight to MENU_LOADING (so we still get the layered
    // menu reveal even when the player skips the story frames).
    requestSkip() {
        const s = get();
        if (s.finished) return;
        if (!s.canSkip) return;
        const menuIdx = SEQUENCE.findIndex((p) => p.phase === PHASE.MENU_LOADING);
        set({
            skipRequested: true,
            phase: PHASE.MENU_LOADING,
            phaseIndex: menuIdx,
            phaseStartedAt: performance.now(),
        });
    },

    unlockSkip() {
        set({ canSkip: true });
    },

    // Directly complete the intro (used when settings disable intro entirely)
    forceComplete() {
        set({
            phase: PHASE.DONE,
            phaseIndex: SEQUENCE.length - 1,
            finished: true,
        });
        markIntroSeen();
    },

    getPhaseDuration() {
        return SEQUENCE[get().phaseIndex]?.duration ?? 0;
    },
}));
